import { useMemo } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useBusiness } from '@/context/BusinessContext';

const STORAGE_KEY = 'pro_business_details';

export interface StoredBusinessDetails {
  businessName: string;
  businessLogoUrl?: string;
  businessType?: string[];
  taxId?: string;
  vatNumber?: string;
  address?: {
    street: string;
    city: string;
    customCity?: string;
    state: string;
    customState?: string;
    zipCode: string;
    country: string;
    customCountry?: string;
  };
  website?: string;
  email?: string;
  phone?: string;
  contactPerson?: {
    name: string;
    role: string;
    phone: string;
    email: string;
  };
  openingHours?: {
    monday: { open: string; close: string; closed: boolean };
    tuesday: { open: string; close: string; closed: boolean };
    wednesday: { open: string; close: string; closed: boolean };
    thursday: { open: string; close: string; closed: boolean };
    friday: { open: string; close: string; closed: boolean };
    saturday: { open: string; close: string; closed: boolean };
    sunday: { open: string; close: string; closed: boolean };
  };
  googleBusinessUrl?: string;
  description?: string;
  serviceArea?: string;
  licenses?: Array<{ type: string; number: string; expiry: string }>;
  socialMedia?: {
    facebook?: string;
    instagram?: string;
    tiktok?: string;
    linkedin?: string;
    twitter?: string;
    whatsapp?: string;
    telegram?: string;
  };
}

export interface ProBusinessDetails extends StoredBusinessDetails {
  id: string;
  clerkId: string | undefined;
  email: string;
  name: string;
  type: string;
  specialization?: string;
  location?: string;
  verified?: boolean;
  rating?: number;
  totalReviews?: number;
}

function getStorageKey(clerkId: string | undefined): string | null {
  return clerkId ? `${STORAGE_KEY}_${clerkId}` : null;
}

export function getStoredBusinessDetails(clerkId: string | undefined): StoredBusinessDetails | null {
  if (typeof document === 'undefined' || !clerkId) return null;
  try {
    const raw = localStorage.getItem(getStorageKey(clerkId) ?? '');
    if (!raw) return null;
    return JSON.parse(raw) as StoredBusinessDetails;
  } catch {
    return null;
  }
}

export function setStoredBusinessDetails(clerkId: string | undefined, details: StoredBusinessDetails): void {
  if (typeof document === 'undefined' || !clerkId) return;
  try {
    localStorage.setItem(getStorageKey(clerkId)!, JSON.stringify(details));
  } catch {
    // ignore
  }
}

export function useProBusinessDetails(): ProBusinessDetails {
  const { user } = useAuth();
  const { business: apiBusiness } = useBusiness();

  return useMemo(() => {
    const clerkId = user?.clerkId;
    const stored = getStoredBusinessDetails(clerkId);

    const base = {
      id: 'prof-1',
      clerkId,
      email: user?.email ?? '',
      name: user?.name ?? '',
      type: 'veterinarian',
      specialization: user?.specialization ?? 'General Practice',
      location: user?.location ?? 'San Francisco, CA',
      verified: user?.verified ?? true,
      rating: user?.rating ?? 4.9,
      totalReviews: user?.totalReviews ?? 127,
    };

    // Priority: API business name > localStorage > Clerk metadata
    const apiBusinessName = (apiBusiness as any)?.name || '';
    const apiBusinessLogo = (apiBusiness as any)?.logoUrl || '';

    if (!stored) {
      return {
        ...base,
        businessName: apiBusinessName || user?.businessName || '',
        businessLogoUrl: apiBusinessLogo || undefined,
      } as ProBusinessDetails;
    }

    return {
      ...base,
      businessName: apiBusinessName || stored.businessName?.trim() || user?.businessName || '',
      businessLogoUrl: apiBusinessLogo || stored.businessLogoUrl || undefined,
      businessType: stored.businessType,
      taxId: stored.taxId,
      vatNumber: stored.vatNumber,
      address: stored.address,
      website: stored.website,
      email: stored.email,
      phone: stored.phone,
      contactPerson: stored.contactPerson,
      openingHours: stored.openingHours,
      googleBusinessUrl: stored.googleBusinessUrl,
      description: stored.description,
      serviceArea: stored.serviceArea,
      licenses: stored.licenses,
      socialMedia: stored.socialMedia,
    } as ProBusinessDetails;
  }, [user, apiBusiness]);
}
